/* eslint-disable max-lines */
import { BigNumber } from '@xylabs/bignumber'
import { EthAddress } from '@xylabs/sdk-js'
import { TypedEvent, XyoWorldMinterEvents } from '@xyo-network/world-typechain'
import { Feature, FeatureCollection } from 'geojson'
// eslint-disable-next-line no-restricted-imports
import { GeoJSONSourceRaw } from 'mapbox-gl'

import { XyoWorldBase } from '../../../Base'
import { BaseJsonObject } from '../../../models'
import { Quadkey } from '../../../Quadkey'
import { MinterDeployBlocks, MinterDeployBlockTimes } from '../DeployBlocks'
import { MinterEvents, MinterEventsJsonObject } from '../Events'
import { MinterContracts } from '../MinterContracts'
import { Actions } from './Actions'
import { DynamicAuctionJsonObject, DynamicData } from './Dynamic'
import { StaticData, StaticJsonObject } from './Static'

export interface AuctionJsonObject extends BaseJsonObject {
  blockNumber?: number
  dynamic: DynamicAuctionJsonObject
  events?: MinterEventsJsonObject
  quadkey: string
  static: StaticJsonObject
}

interface Flowed {
  in: BigNumber | null
  out: BigNumber | null
}

export class XyoWorldAuction extends XyoWorldBase<AuctionJsonObject> {
  public actions: Actions

  public blockNumber?: number

  public dynamic: DynamicData

  public quadkey: Quadkey

  public static: StaticData

  private _burned?: BigNumber | null

  private _dutchAuctionPrice: BigNumber | undefined

  private _events?: MinterEvents

  private _flowed?: Flowed

  private _mintedBy?: EthAddress

  private minters: MinterContracts

  constructor(quadkey: Quadkey, minters: MinterContracts, jsonObject?: AuctionJsonObject) {
    super()
    this.quadkey = quadkey
    this.minters = minters
    this.actions = new Actions(quadkey, minters.current)
    this.dynamic = new DynamicData(this.quadkey)
    this.static = new StaticData(this.quadkey)
    this.loadJsonObject(jsonObject)
  }

  static getBidderCount(events: XyoWorldMinterEvents.BidPlaced[]) {
    const bidders = new Set()
    for (const event of events) {
      bidders.add(event.args.by)
    }
    return bidders.size
  }

  public canMint(address?: EthAddress | null) {
    if (this.isCompleted()) {
      return this.dynamic.bidder?.equals(address)
    }
    return false
  }

  public canMintForReward() {
    if (this.isCompleted()) {
      return this.dynamic.canMintForReward || this.hasBeen24HoursSinceEnd()
    }
    return false
  }

  public clearDynamicData() {
    this.log('clearDynamicData')
    this.dynamic = new DynamicData(this.quadkey)
  }

  public currentBid() {
    return this.dynamic.currentBid
  }

  public dutchAuctionPrice() {
    const { endTime } = this.static
    if (endTime) {
      const secondsSinceEnd = Date.now() / 1000 - endTime
      const { startingBid } = this.static
      if (secondsSinceEnd && startingBid) {
        const age = secondsSinceEnd
        this._dutchAuctionPrice = this._dutchAuctionPrice ?? this.calcDutchAuctionPrice(startingBid, age)
      }
    }
    return this._dutchAuctionPrice
  }

  public estimatedBlockTime(blockNumber: number) {
    const deployBlockNumber = MinterDeployBlocks.v3
    const avgBlockTime = 13.37
    const deployTime = MinterDeployBlockTimes.v3
    const estimatedTime = (blockNumber - deployBlockNumber) * avgBlockTime + deployTime
    return estimatedTime
  }

  public events() {
    this._events = this._events || new MinterEvents(this.minters, { quadkey: this.quadkey })
    return this._events
  }

  public async fetchBidFee(amount: BigNumber) {
    this.log('fetchBidFee')
    return await this.minters.current.bidFee(this.quadkey, amount)
  }

  public async fetchBidder() {
    this.log('fetchBidder')
    this.dynamic.bidder = await this.minters.current.bidder(this.quadkey)
    return this.dynamic.bidder
  }

  public async fetchBidderCount() {
    this.log('fetchBidderCount')
    return XyoWorldAuction.getBidderCount(await this.events().bidPlaced())
  }

  public async fetchBurned() {
    this._burned = new BigNumber(0)
    const events = await this.events().tokensBurned()
    if (events) {
      for (const event of events) {
        this._burned = this._burned.add(new BigNumber(event.args.amount.toString()))
      }
    }
    return this._burned
  }

  public async fetchBuyer() {
    if (this.static?.minted) {
      const event = (await this.events().bidPlaced()).pop()
      return EthAddress.fromString(event?.args.by)
    }
  }

  public async fetchCanMintForReward() {
    this.log('fetchCanMintForReward')
    return (this.dynamic.canMintForReward = await this.minters.current.canMintForReward(this.quadkey))
  }

  public async fetchCost() {
    if (this.isMinted()) {
      const event = (await this.events().bidPlaced()).pop()
      return (this.static.cost = new BigNumber(event?.args.amount?.toString() ?? 0))
    }
  }

  public async fetchCurrentBid() {
    this.log('fetchCurrentBid')
    this.dynamic.currentBid = await this.minters.current.currentBid(this.quadkey)
    return this.dynamic.currentBid
  }

  public async fetchDutchAuctionActive() {
    this.log('fetchDutchAuctionActive')
    this.dynamic.dutchAuctionActive = await this.minters.current.dutchAuctionActive(this.quadkey)
    return this.dynamic.dutchAuctionActive
  }

  public async fetchDutchAuctionPrice() {
    this.log('fetchDutchAuctionPrice')
    this.dynamic.dutchAuctionPrice = await this.minters.current.dutchAuctionPrice(this.quadkey)
    return this.dynamic.dutchAuctionPrice
  }

  public async fetchEndTime() {
    this.static.endTime = (await this.minters.current.endTime(this.quadkey)).toNumber()
    return this.static.endTime
  }

  public async fetchFlowed() {
    if (this.isMinted()) {
      const flowed = {
        in: new BigNumber(0),
        out: new BigNumber(0),
      }
      const events = await this.events().tokensFlowed()
      if (events) {
        for (const event of events) {
          const fromBN = new BigNumber(event.args.from.toString())
          const toBN = new BigNumber(event.args.from.toString())
          const quadkeyBN = this.quadkey.toBigNumber()
          if (!fromBN.eq(toBN)) {
            if (toBN.eq(quadkeyBN)) {
              flowed.out = flowed.out.add(new BigNumber(event.args.amount.toString()))
            } else {
              flowed.in = flowed.in.add(new BigNumber(event.args.amount.toString()))
            }
          }
        }
      }
      this._flowed = flowed
    } else {
      this._flowed = { in: null, out: null }
    }
    return this._flowed
  }

  public async fetchHasBid() {
    this.log('fetchHasBid')
    if (this.static?.started) {
      return await this.minters.current.hasBid(this.quadkey)
    }
  }

  public async fetchMinimumBid() {
    this.log('fetchMinimumBid')
    this.dynamic.minimumBid = await this.minters.current.minimumBid(this.quadkey)
    return this.dynamic.minimumBid
  }

  public async fetchMintNowPrice() {
    this.log('fetchMintNowPrice')
    this.dynamic.mintNowPrice = await this.minters.current.mintNowPrice(this.quadkey)
    return this.dynamic.mintNowPrice
  }

  public async fetchMinter() {
    this.log('fetchMinter')
    if (this.static?.minted) {
      const event = (await this.events().geoTokenMinted()).pop()
      return EthAddress.fromString(event?.args.by)
    }
  }

  public async fetchMintingReward() {
    this.log('fetchMintingReward')
    if (this.isMinted()) {
      const amount = (await this.events().mintingFeeSent()).pop()?.args.amount?.toString()
      return new BigNumber(amount ?? 0)
    }
    if (this.isCompleted()) {
      return this.price()?.muln(0.1)
    }
  }

  public async getBidder() {
    return this.dynamic.bidder ?? (await this.fetchBidder())
  }

  public async getBurned() {
    return this._burned ?? (await this.fetchBurned())
  }

  public async getBuyer() {
    this.static.buyer = this.static.buyer ?? (await this.fetchBuyer())
    return this.static.buyer
  }

  public async getCanMintForReward() {
    return this.canMintForReward() ?? (await this.fetchCanMintForReward())
  }

  public async getCost() {
    this.static.cost = this.static.cost ?? (await this.fetchCost())
    return this.static.cost
  }

  public async getCurrentBid() {
    return this.dynamic.currentBid === undefined ? await this.fetchCurrentBid() : this.dynamic.currentBid
  }

  public async getDutchAuctionActive() {
    return this.dynamic.dutchAuctionActive ?? (await this.fetchDutchAuctionActive())
  }

  public async getDutchAuctionPrice() {
    return this.dynamic.dutchAuctionPrice === undefined ? await this.fetchDutchAuctionPrice() : this.dynamic.dutchAuctionPrice
  }

  public async getEndTime() {
    return this.static.endTime ?? (await this.fetchEndTime())
  }

  public async getFlowed() {
    return this._flowed ?? (await this.fetchFlowed())
  }

  public async getHasBid() {
    this.static.hasBid = this.static.hasBid ?? (await this.fetchHasBid())
    return this.static.hasBid
  }

  public async getMinimumBid() {
    return this.dynamic.minimumBid === undefined ? await this.fetchMinimumBid() : this.dynamic.minimumBid
  }

  public async getMintNowPrice() {
    return this.dynamic.mintNowPrice === undefined ? await this.fetchMintNowPrice() : this.dynamic.mintNowPrice
  }

  public async getMintedBy() {
    this._mintedBy = this._mintedBy ?? EthAddress.fromString((await this.events().geoTokenMinted()).pop()?.args.by)
    return this._mintedBy
  }

  public async getMinter() {
    this.static.minter = this.static.minter ?? (await this.fetchMinter())
    return this.static.minter
  }

  public async getMintingReward() {
    this.static.mintingReward = this.static.mintingReward ?? (await this.fetchMintingReward())
    return this.static.mintingReward
  }

  public async getProfit() {
    let profit = null
    const buyer = await this.getBuyer()
    if (this.isMinted()) {
      const flowed = await this.getFlowed()
      profit = (flowed.in ?? new BigNumber(0))
        .sub(flowed.out ?? new BigNumber(0))
        .sub((await this.getBurned()) ?? new BigNumber(0))
        .sub(buyer?.equals(await this.getMintedBy()) ? new BigNumber(0) : this.static.mintingReward ?? new BigNumber(0))
    }
    return profit
  }

  public isCompleted() {
    return this.static.isCompleted()
  }

  public isDutch() {
    return this.static.isDutch()
  }

  public isExpired() {
    return this.static.isExpired()
  }

  public isLive() {
    return this.static.isLive()
  }

  public isMinted() {
    return this.static.isMinted()
  }

  public isUpcoming() {
    return this.static.isUpcoming()
  }

  public loadJsonObject(jsonObject?: AuctionJsonObject) {
    if (jsonObject) {
      this.blockNumber = jsonObject.blockNumber
      this.static.loadJsonObject(jsonObject.static)
      this.dynamic.loadJsonObject(jsonObject.dynamic)
    }
    return this
  }

  public price() {
    if (this.static.isDutch()) {
      return this.dutchAuctionPrice()
    }
    if (this.static.isLive()) {
      return this.dynamic.minimumBid ?? this.dynamic.currentBid?.muln(1.1) ?? this.static.startingBid
    }
    if (this.static.isCompleted()) {
      return this.dynamic.currentBid
    }
    if (this.static.isMinted()) {
      return this.dynamic.currentBid
    }
  }

  public async refresh() {
    this.log('refresh')
    this.blockNumber = undefined
    this.static = new StaticData(this.quadkey)
    this.dynamic = new DynamicData(this.quadkey)
    await this.update()
  }

  public async refreshDirect() {
    this.warn('refreshDirect - only call this for testing')
    await this.refreshStaticDataDirect()
    await this.refreshDynamicDataDirect()
    return this
  }

  public async refreshDynamicDataDirect() {
    this.warn('refreshDynamicDataDirect - only call this for testing')
    this.dynamic = new DynamicData(this.quadkey)
    const dest = this.dynamic || {}
    await Promise.all([
      (async () => (dest.bidder = dest.bidder || (await this.minters.current.bidder(this.quadkey))))(),
      (async () => (dest.canMintForReward = dest.canMintForReward || (await this.minters.current.canMintForReward(this.quadkey))))(),
      (async () => (dest.currentBid = dest.currentBid || (await this.minters.current.currentBid(this.quadkey))))(),
      (async () => (dest.dutchAuctionActive = dest.dutchAuctionActive || (await this.minters.current.dutchAuctionActive(this.quadkey))))(),
      (async () => (dest.dutchAuctionPrice = dest.dutchAuctionPrice || (await this.minters.current.dutchAuctionPrice(this.quadkey))))(),
      (async () => (dest.minimumBid = dest.minimumBid || (await this.minters.current.minimumBid(this.quadkey))))(),
      (async () => (dest.mintNowPrice = dest.mintNowPrice || (await this.minters.current.mintNowPrice(this.quadkey))))(),
    ])
    this.dynamic = dest
    return dest
  }

  public async refreshStaticDataDirect() {
    this.warn('refreshStaticDataDirect - only call this for testing')
    this.static = new StaticData(this.quadkey, {})
    const dest = this.static
    await Promise.all([
      (async () => (dest.started = dest.started ?? (await this.minters.current.started(this.quadkey))))(),
      (async () => (dest.minted = dest.minted ?? (await this.minters.current.minted(this.quadkey))))(),
      (async () => (dest.endTime = dest.endTime ?? (await this.minters.current.endTime(this.quadkey)).toNumber()))(),
    ])
    if (dest.started) {
      if (dest.minted) {
        await Promise.all([
          (async () => (dest.cost = dest.cost ?? (await this.getCost())))(),
          (async () => (dest.buyer = dest.buyer ?? (await this.getBuyer())))(),
          (async () => (dest.minter = dest.minter ?? (await this.getMinter())))(),
          (async () => (dest.mintingReward = dest.mintingReward ?? (await this.getMintingReward())))(),
        ])
      } else {
        dest.hasBid = dest.hasBid || (await this.getHasBid())
      }
    }
    this.static = dest
    return dest
  }

  public status() {
    return this.static.status()
  }

  public toGeoJsonPointFeature(): Feature {
    return {
      geometry: this.quadkey.geoJson().point(),
      properties: this.toJsonObject(),
      type: 'Feature',
    }
  }

  public toGeoJsonPointFeatureCollection(): FeatureCollection {
    return {
      features: [this.toGeoJsonPointFeature()],
      type: 'FeatureCollection',
    }
  }

  public toGeoJsonPointSource(): GeoJSONSourceRaw {
    return {
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 250,
      data: this.toGeoJsonPointFeatureCollection(),
      type: 'geojson',
    }
  }

  public toGeoJsonPolygonFeature(): Feature {
    return {
      geometry: this.quadkey.geoJson().polygon(),
      properties: this.toJsonObject(),
      type: 'Feature',
    }
  }

  public toGeoJsonPolygonFeatureCollection(): FeatureCollection {
    return {
      features: [this.toGeoJsonPolygonFeature()],
      type: 'FeatureCollection',
    }
  }

  public toGeoJsonPolygonSource(): GeoJSONSourceRaw {
    return {
      data: this.toGeoJsonPolygonFeatureCollection(),
      type: 'geojson',
    }
  }

  public toJsonObject(): AuctionJsonObject {
    return {
      blockNumber: this.blockNumber,
      dynamic: this.dynamic?.toJsonObject(),
      quadkey: this.quadkey.toBase4HashLabel(),
      static: this.static?.toJsonObject(),
    }
  }

  public toJsonObjectWithEvents(): AuctionJsonObject {
    return { events: this.events()?.toJsonObject(), ...this.toJsonObject() }
  }

  public async update() {
    this.log('update')
    const currentBlockNumber = await this.minters.current.contract.provider.getBlockNumber()
    if (this.blockNumber ?? currentBlockNumber > 0) {
      const events = await this.events().all(this.blockNumber ?? 0 + 1)
      if (events.length > 0) {
        await this.updateWithEvents(events, currentBlockNumber)
      }
    }
    this.blockNumber = currentBlockNumber
    return this
  }

  // we only apply the event if the blockNumber is newer
  public async updateWithEvent(event: TypedEvent) {
    if (event.blockNumber > (this.blockNumber ?? 0)) {
      switch (event.event) {
        case 'AuctionStarted':
          this.static.started = true
          this.static.auctionLength = event.args.length
          this.static.endTime = await this.getEndTime()
          if (this.static.endTime) {
            this.static.startTime = this.static.endTime - (this.static.auctionLength ?? 0)
          }
          this.static.startingBid = event.args.amount
          this.static.startBlock = event.args.blockNumber
          break
        case 'BidPlaced':
          this.static.hasBid = true
          this.dynamic.bidder = event.args.by
          this.dynamic.currentBid = event.args.amount
          break
        case 'GeotokenMinted':
          this.static.minted = true
          this.static.minter = event.args.by
          this.static.buyer = this.dynamic.bidder
          break
        case 'MintingFeeSent':
          this.static.mintingReward = event.args.amount
          break
      }
    } else {
      // console.warn(`Attempt to update ${this.quadkey} with old event(s) ${this.blockNumber}>=${event.blockNumber}`)
    }
    return this
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public async updateWithEvents(events: any[], currentBlock?: number) {
    let newBlockNumber = currentBlock ?? this.blockNumber
    for (const event of events) {
      await this.updateWithEvent(event)
      if (event.blockNumber > (newBlockNumber ?? 0)) {
        newBlockNumber = event.blockNumber
      }
    }
    this.events().load(events, newBlockNumber)
    // we update the block number after all the events are applied
    this.blockNumber = newBlockNumber
    return this
  }

  // age is in seconds
  // eslint-disable-next-line max-statements
  private calcDutchAuctionPrice(startingBid: BigNumber, age: number): BigNumber {
    let remainingAge = new BigNumber(Math.floor(age / 15)) // seconds to minutes, but switch to quarter from year
    let remainingPrice = startingBid

    const MINUTES_IN_HOUR = 60
    const MINUTES_IN_DAY = 24 * MINUTES_IN_HOUR
    const MINUTES_IN_WEEK = 7 * MINUTES_IN_DAY
    const MINUTES_IN_QUARTER = 13 * MINUTES_IN_WEEK
    const MINUTES_IN_YEAR = 4 * MINUTES_IN_QUARTER

    const DUTCH_DROP_YEAR_PERCENT = 16
    const DUTCH_DROP_QUARTER_PERCENT = 11247
    const DUTCH_DROP_WEEK_PERCENT = 84528
    const DUTCH_DROP_DAY_PERCENT = 97627
    const DUTCH_DROP_HOUR_PERCENT = 99900

    const MICRO_PERCENT_DIV = 100000

    const dropYears = Math.floor(remainingAge.divn(MINUTES_IN_YEAR).toNumber())
    remainingAge = remainingAge.subn(dropYears * MINUTES_IN_YEAR)

    const dropQuarters = Math.floor(remainingAge.divn(MINUTES_IN_QUARTER).toNumber())
    remainingAge = remainingAge.subn(dropQuarters * MINUTES_IN_QUARTER)

    const dropWeeks = Math.floor(remainingAge.divn(MINUTES_IN_WEEK).toNumber())
    remainingAge = remainingAge.subn(dropWeeks * MINUTES_IN_WEEK)

    const dropDays = Math.floor(remainingAge.divn(MINUTES_IN_DAY).toNumber())
    remainingAge = remainingAge.subn(dropDays * MINUTES_IN_DAY)

    const dropHours = Math.floor(remainingAge.divn(MINUTES_IN_HOUR).toNumber())
    remainingAge = remainingAge.subn(dropHours * MINUTES_IN_HOUR)

    for (let x = 0; x < dropYears; x++) {
      remainingPrice = remainingPrice.muln(DUTCH_DROP_YEAR_PERCENT).divn(MICRO_PERCENT_DIV)
    }

    for (let x = 0; x < dropQuarters; x++) {
      remainingPrice = remainingPrice.muln(DUTCH_DROP_QUARTER_PERCENT).divn(MICRO_PERCENT_DIV)
    }

    for (let x = 0; x < dropWeeks; x++) {
      remainingPrice = remainingPrice.muln(DUTCH_DROP_WEEK_PERCENT).divn(MICRO_PERCENT_DIV)
    }

    for (let x = 0; x < dropDays; x++) {
      remainingPrice = remainingPrice.muln(DUTCH_DROP_DAY_PERCENT).divn(MICRO_PERCENT_DIV)
    }

    for (let x = 0; x < dropHours; x++) {
      remainingPrice = remainingPrice.muln(DUTCH_DROP_HOUR_PERCENT).divn(MICRO_PERCENT_DIV)
    }

    if (remainingPrice.eqn(0)) {
      remainingPrice = new BigNumber(1)
    }

    return remainingPrice
  }

  private hasBeen24HoursSinceEnd() {
    const secondInDay = 60 * 60 * 24
    const now = Date.now() / 1000
    const endTime = this.static.endTime ?? now
    return now - endTime > secondInDay
  }

  private log(message: string) {
    return console.log(`Auction: ${message} [${this.quadkey.toBase4HashLabel()}]`)
  }

  private warn(message: string) {
    return console.warn(`Auction: ${message} [${this.quadkey.toBase4HashLabel()}]`)
  }
}
